import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, EVENTS_TABLE } from '../config/supabase';

const EventContext = createContext();

export const useEvents = () => {
    const context = useContext(EventContext);
    if (!context) {
        throw new Error('useEvents must be used within an EventProvider');
    }
    return context;
};

export const EventProvider = ({ children }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Helper function to parse date string (e.g., "5TH NOV, WED" or "7th Nov FRI") to Date object
    const parseEventDate = (dateString) => {
        if (!dateString || typeof dateString !== 'string') {
            return new Date(0); // Return epoch date for invalid dates (will sort to end)
        }

        try {
            // Remove day of week suffix (e.g., ", WED" or " FRI")
            const cleanedDate = dateString.replace(/,\s*(MON|TUE|WED|THU|FRI|SAT|SUN)$/i, '')
                                          .replace(/\s+(MON|TUE|WED|THU|FRI|SAT|SUN)$/i, '')
                                          .trim();

            // Extract day and month (e.g., "5TH NOV" or "7th Nov")
            const match = cleanedDate.match(/(\d+)(?:ST|ND|RD|TH)?\s+([A-Za-z]+)/i);
            if (!match) {
                return new Date(0);
            }

            const day = parseInt(match[1], 10);
            const monthName = match[2].toLowerCase();
            
            // Map month names to numbers
            const monthMap = {
                'jan': 0, 'january': 0,
                'feb': 1, 'february': 1,
                'mar': 2, 'march': 2,
                'apr': 3, 'april': 3,
                'may': 4,
                'jun': 5, 'june': 5,
                'jul': 6, 'july': 6,
                'aug': 7, 'august': 7,
                'sep': 8, 'september': 8,
                'oct': 9, 'october': 9,
                'nov': 10, 'november': 10,
                'dec': 11, 'december': 11
            };

            const month = monthMap[monthName];
            if (month === undefined) {
                return new Date(0);
            }

            // Get current year, or next year if the date has passed
            const now = new Date();
            const currentYear = now.getFullYear();
            
            // Create date with current year (set to midnight for accurate comparison)
            const eventDateThisYear = new Date(currentYear, month, day);
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            
            // If the date with current year is in the past, assume next year
            let year = currentYear;
            if (eventDateThisYear < today) {
                year = currentYear + 1;
            }

            return new Date(year, month, day);
        } catch (err) {
            console.warn('Failed to parse date:', dateString, err);
            return new Date(0);
        }
    };

    // Sort events by parsed date (earliest first)
    const sortEventsByDate = (eventsList) => {
        return [...eventsList].sort((a, b) => {
            const dateA = parseEventDate(a.date);
            const dateB = parseEventDate(b.date);
            // Sort ascending (earliest date first)
            return dateA.getTime() - dateB.getTime();
        });
    };

    // Load events from Supabase on mount
    useEffect(() => {
        const loadEvents = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // Fetch events from Supabase table
                const { data, error: fetchError } = await supabase
                    .from(EVENTS_TABLE)
                    .select('*');
                
                if (fetchError) {
                    throw fetchError;
                }
                
                // Sort events by date (most recent first)
                const sortedEvents = sortEventsByDate(data || []);
                setEvents(sortedEvents);
                setError(null);
            } catch (err) {
                console.error('Error loading events:', err);
                
                // Provide more specific error messages
                let errorMessage = 'Failed to load events. ';
                if (err.message && err.message.includes('permission')) {
                    errorMessage += 'Please check Supabase table permissions.';
                } else if (err.message && err.message.includes('relation') || err.message.includes('does not exist')) {
                    errorMessage += 'Please create the events table in Supabase.';
                } else {
                    errorMessage += 'Please refresh the page or check your connection.';
                }
                
                setError(errorMessage);
                // Set empty array on error so UI can show placeholders
                setEvents([]);
            } finally {
                setLoading(false);
            }
        };

        loadEvents();
    }, []);

    const addEvent = async (eventData) => {
        try {
            // Prepare clean event data matching Supabase table schema
            // Table has: id (uuid), title (text), date (text), image_url (text), reservation (text), created_at (timestamptz)
            const newEvent = {
                date: eventData.date || '', // Display date string like "7th Nov FRI"
                title: eventData.title,
                image_url: eventData.image || '',
                reservation: eventData.reservationLink || eventData.reservation || '/reservation',
                vip_link: eventData.vipLink || '',
                general_link: eventData.generalLink || ''
            };
            
            // Only add timestamps if table has these columns
            // Supabase will auto-generate if set as default
            const { data, error: insertError } = await supabase
                .from(EVENTS_TABLE)
                .insert([newEvent])
                .select();
            
            if (insertError) {
                console.error('Supabase insert error:', insertError);
                console.error('Event data attempted:', newEvent);
                throw insertError;
            }
            
            if (!data || data.length === 0) {
                throw new Error('No data returned from insert');
            }
            
            // Update local state - sort after adding
            const addedEvent = data[0];
            setEvents((prevEvents) => sortEventsByDate([addedEvent, ...prevEvents]));
            
            return addedEvent;
        } catch (err) {
            console.error('Error adding event:', err);
            console.error('Error details:', {
                message: err.message,
                code: err.code,
                details: err.details,
                hint: err.hint
            });
            
            let errorMessage = 'Failed to add event. ';
            if (err.message && err.message.includes('invalid input syntax for type timestamp')) {
                errorMessage += 'The date column in Supabase is still set as timestamp. Please change it to text type in Supabase Table Editor.';
            } else if (err.message && err.message.includes('relation') || err.message.includes('does not exist')) {
                errorMessage += 'Please create the events table in Supabase.';
            } else if (err.message && err.message.includes('permission') || err.message.includes('RLS') || err.code === '42501') {
                errorMessage += 'Row Level Security (RLS) is blocking the insert. Please disable RLS or create policies in Supabase Table Editor → events → Policies.';
            } else if (err.message && err.message.includes('column')) {
                errorMessage += 'Table column mismatch. Check table schema.';
            } else {
                errorMessage += err.message || 'Please try again.';
            }
            
            setError(errorMessage);
            throw err;
        }
    };

    const updateEvent = async (id, updatedData) => {
        try {
            // Prepare clean update data matching table schema
            const updateData = {
                date: updatedData.date || '', // Display date string
                title: updatedData.title,
                image_url: updatedData.image || updatedData.image_url || '',
                reservation: updatedData.reservationLink || updatedData.reservation || '/reservation',
                vip_link: updatedData.vipLink || '',
                general_link: updatedData.generalLink || ''
            };
            
            const { data, error: updateError } = await supabase
                .from(EVENTS_TABLE)
                .update(updateData)
                .eq('id', id)
                .select();
            
            if (updateError) {
                console.error('Supabase update error:', updateError);
                throw updateError;
            }
            
            if (!data || data.length === 0) {
                throw new Error('No data returned from update');
            }
            
            // Update local state - sort after updating
            setEvents((prevEvents) => {
                const updated = prevEvents.map((event) =>
                    event.id === id ? data[0] : event
                );
                return sortEventsByDate(updated);
            });
        } catch (err) {
            console.error('Error updating event:', err);
            setError('Failed to update event. Please try again.');
            throw err;
        }
    };

    const deleteEvent = async (id) => {
        try {
            const { error: deleteError } = await supabase
                .from(EVENTS_TABLE)
                .delete()
                .eq('id', id);
            
            if (deleteError) {
                throw deleteError;
            }
            
            // Update local state
            setEvents((prevEvents) => prevEvents.filter((event) => event.id !== id));
        } catch (err) {
            console.error('Error deleting event:', err);
            setError('Failed to delete event. Please try again.');
            throw err;
        }
    };

    const value = {
        events,
        loading,
        error,
        addEvent,
        updateEvent,
        deleteEvent,
    };

    return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
};

